import { useState, useEffect, useCallback } from 'react';
import { SlidersHorizontal, ArrowUpCircle, ArrowDownCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import { cantidad } from '../utils/format';

// ── Config de tipos de movimiento ─────────────────────────────────────────
const TIPOS = {
  compra:         { label: 'Compra',         color: 'bg-blue-100  text-blue-700',  signo: '+' },
  venta:          { label: 'Venta',          color: 'bg-orange-100 text-orange-700', signo: '-' },
  merma:          { label: 'Merma',          color: 'bg-red-100   text-red-700',   signo: '-' },
  ajuste_entrada: { label: 'Ajuste entrada', color: 'bg-teal-100  text-teal-700',  signo: '+' },
  ajuste_salida:  { label: 'Ajuste salida',  color: 'bg-amber-100 text-amber-700', signo: '-' },
};

const TIPOS_AJUSTE = [
  { value: 'ajuste_entrada', label: 'Entrada (corrección / devolución)' },
  { value: 'ajuste_salida',  label: 'Salida (corrección)' },
  { value: 'merma',          label: 'Merma (pérdida / vencimiento)' },
];

function TipoBadge({ tipo }) {
  const cfg = TIPOS[tipo] ?? { label: tipo, color: 'bg-gray-100 text-gray-600', signo: '' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>;
}

// ── Modal ajuste ──────────────────────────────────────────────────────────
function AjusteModal({ isOpen, onClose, productos, onSuccess }) {
  const [form, setForm]     = useState({ producto_id: '', tipo: 'ajuste_entrada', cantidad: '', notas: '' });
  const [saving, setSaving] = useState(false);

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const esSalida = ['ajuste_salida', 'merma'].includes(form.tipo);
  const prod     = productos.find(p => p.id === parseInt(form.producto_id));

  const handleSave = async () => {
    if (!form.producto_id) { toast.error('Seleccioná un producto'); return; }
    if (!form.cantidad || parseFloat(form.cantidad) <= 0) { toast.error('La cantidad debe ser mayor a 0'); return; }
    if (esSalida && !form.notas.trim()) { toast.error('El motivo es obligatorio para salidas y mermas'); return; }

    setSaving(true);
    try {
      const res = await api.post('/stock/ajuste.php', {
        producto_id: parseInt(form.producto_id),
        tipo:        form.tipo,
        cantidad:    parseFloat(form.cantidad),
        notas:       form.notas.trim(),
      });
      toast.success(`Ajuste registrado — Stock nuevo: ${cantidad(res.data.stock_despues, prod?.unidad)}`);
      setForm({ producto_id: '', tipo: 'ajuste_entrada', cantidad: '', notas: '' });
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Error al registrar ajuste');
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Registrar ajuste de stock" size="md"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Registrando...' : 'Registrar ajuste'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label>Producto *</label>
          <select className="input" value={form.producto_id} onChange={set('producto_id')}>
            <option value="">Seleccionar producto...</option>
            {productos.map(p => (
              <option key={p.id} value={p.id}>
                {p.nombre} — stock actual: {cantidad(p.stock_actual, p.unidad)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Tipo de ajuste *</label>
          <div className="space-y-2">
            {TIPOS_AJUSTE.map(t => (
              <label key={t.value} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:border-primary-300 transition-colors">
                <input type="radio" name="tipo" value={t.value} checked={form.tipo === t.value} onChange={set('tipo')} className="accent-primary-500" />
                <div className="flex items-center gap-2 text-sm">
                  {['ajuste_entrada'].includes(t.value)
                    ? <ArrowUpCircle size={16} className="text-teal-500" />
                    : <ArrowDownCircle size={16} className="text-red-500" />}
                  {t.label}
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label>Cantidad *</label>
          <input className="input" type="number" min="0.001" step="0.001"
            value={form.cantidad} onChange={set('cantidad')} placeholder="0" />
        </div>

        <div>
          <label>{esSalida ? 'Motivo *' : 'Notas (opcional)'}</label>
          <textarea className="input resize-none" rows={2}
            placeholder={esSalida ? 'Describí el motivo de la salida...' : 'Observaciones...'}
            value={form.notas} onChange={set('notas')} />
        </div>

        {prod && (
          <div className={`p-3 rounded-xl text-sm ${esSalida ? 'bg-red-50 text-red-700' : 'bg-teal-50 text-teal-700'}`}>
            Stock actual de <strong>{prod.nombre}</strong>: {cantidad(prod.stock_actual, prod.unidad)}
            {form.cantidad && (
              <span className="ml-2 font-bold">
                → {cantidad(
                  esSalida
                    ? prod.stock_actual - parseFloat(form.cantidad)
                    : prod.stock_actual + parseFloat(form.cantidad),
                  prod.unidad
                )}
              </span>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function Stock() {
  const [movimientos, setMovimientos] = useState([]);
  const [productos, setProductos]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [ajusteOpen, setAjusteOpen]   = useState(false);

  const [filtros, setFiltros] = useState({
    search: '', tipo: '', fecha_desde: '', fecha_hasta: '',
  });

  const setFiltro = (f) => (e) => setFiltros(p => ({ ...p, [f]: e.target.value }));

  const fetchMovimientos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.search)      params.search      = filtros.search;
      if (filtros.tipo)        params.tipo        = filtros.tipo;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;

      const res = await api.get('/stock/movimientos.php', { params });
      setMovimientos(res.data);
    } catch { toast.error('Error al cargar movimientos'); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { fetchMovimientos(); }, [fetchMovimientos]);

  useEffect(() => {
    api.get('/productos/index.php')
      .then(r => setProductos(r.data))
      .catch(() => {});
  }, []);

  // Stats en base a últimos movimientos (aproximado; el exacto está en el dashboard)
  const sinStock  = productos.filter(p => p.stock_actual <= 0).length;
  const bajoStock = productos.filter(p => p.stock_minimo > 0 && p.stock_actual <= p.stock_minimo && p.stock_actual > 0).length;

  const limpiarFiltros = () => setFiltros({ search: '', tipo: '', fecha_desde: '', fecha_hasta: '' });
  const hayFiltros = Object.values(filtros).some(Boolean);

  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Stock</h1>
        <button className="btn-primary" onClick={() => setAjusteOpen(true)}>
          <SlidersHorizontal size={18} /> Registrar ajuste
        </button>
      </div>

      {/* Mini stats */}
      {productos.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="card-sm text-center">
            <p className="text-2xl font-bold text-red-600">{sinStock}</p>
            <p className="text-xs text-gray-400 mt-0.5">Sin stock</p>
          </div>
          <div className="card-sm text-center">
            <p className="text-2xl font-bold text-orange-500">{bajoStock}</p>
            <p className="text-xs text-gray-400 mt-0.5">Bajo stock</p>
          </div>
          <div className="card-sm text-center">
            <p className="text-2xl font-bold text-green-600">{productos.length - sinStock - bajoStock}</p>
            <p className="text-xs text-gray-400 mt-0.5">Stock OK</p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="card !p-4">
        <div className="flex flex-wrap gap-3">
          <input className="input flex-1 min-w-40" placeholder="Buscar producto..."
            value={filtros.search} onChange={setFiltro('search')} />
          <select className="input w-44" value={filtros.tipo} onChange={setFiltro('tipo')}>
            <option value="">Todos los tipos</option>
            {Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <input className="input w-40" type="date" title="Desde"
            value={filtros.fecha_desde} onChange={setFiltro('fecha_desde')} />
          <input className="input w-40" type="date" title="Hasta"
            value={filtros.fecha_hasta} onChange={setFiltro('fecha_hasta')} />
          {hayFiltros && (
            <button className="btn-ghost text-sm text-gray-400" onClick={limpiarFiltros}>Limpiar</button>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : movimientos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-300">
            <RefreshCw size={48} />
            <p className="text-sm mt-3 text-gray-400">
              {hayFiltros ? 'Sin resultados con esos filtros' : 'No hay movimientos registrados'}
            </p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Fecha / Hora</th>
                    <th>Producto</th>
                    <th>Tipo</th>
                    <th>Cantidad</th>
                    <th>Stock antes</th>
                    <th>Stock después</th>
                    <th>Usuario</th>
                    <th>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map(m => {
                    const cfg  = TIPOS[m.tipo] ?? { signo: '', color: '' };
                    const esSalida = cfg.signo === '-';
                    const fecha = new Date(m.fecha).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
                    return (
                      <tr key={m.id}>
                        <td className="tabular-nums text-gray-500 text-xs whitespace-nowrap">{fecha}</td>
                        <td className="font-medium text-gray-900">{m.producto}</td>
                        <td><TipoBadge tipo={m.tipo} /></td>
                        <td className={`tabular-nums font-semibold ${esSalida ? 'text-red-600' : 'text-teal-600'}`}>
                          {cfg.signo}{cantidad(m.cantidad, m.unidad)}
                        </td>
                        <td className="tabular-nums text-gray-400">{cantidad(m.stock_antes, m.unidad)}</td>
                        <td className="tabular-nums font-medium text-gray-700">{cantidad(m.stock_despues, m.unidad)}</td>
                        <td className="text-gray-400 text-xs">{m.usuario}</td>
                        <td className="text-gray-400 text-xs max-w-xs truncate" title={m.notas}>{m.notas || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">{movimientos.length} movimiento/s (últimos 500)</p>
            </div>
          </>
        )}
      </div>

      <AjusteModal
        isOpen={ajusteOpen}
        onClose={() => setAjusteOpen(false)}
        productos={productos}
        onSuccess={fetchMovimientos}
      />
    </div>
  );
}
